import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ClaimItem, ObjectType} from '../../entity/claim-item';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatChip} from '@angular/material/chips';
import {CommonModule, DatePipe} from '@angular/common';
import {ClaimListService} from '../../control/claim-list-service';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';

@Component({
  selector: 'app-claim-list',
  imports: [
    CommonModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatHeaderCellDef,
    MatIconButton,
    MatIcon,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatChip,
    DatePipe,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatLabel,
    MatInput,
    LocalizationPipePipe
  ],
  templateUrl: './claim-list.html',
  styleUrl: './claim-list.scss'
})
export class ClaimList implements OnInit, OnChanges, AfterViewInit {
  @Output() onPageChange = new EventEmitter<PageEvent>();
  @Input() claims: ClaimItem[] | null = null;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  displayedColumns = ['id', 'deed', 'adjustmentMethod', 'reviewed', 'status', 'actions'];
  columnsToDisplayWithExpand = [...this.displayedColumns, 'expand'];
  expandedElement: ClaimItem | null = null;
  visibleClaims: ClaimItem[] = [];
  dataSource = new MatTableDataSource<ClaimItem>(this.visibleClaims);

  constructor(private claimListService: ClaimListService) {
  }

  get numberOfClaims(): number {
    return this.visibleClaims?.length ?? 0;
  }

  ngOnInit(): void {
    this.updateVisibleClaims();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateVisibleClaims();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  private updateVisibleClaims() {
    this.visibleClaims = this.claims?.filter(c => c.status !== 'new') ?? [];
    this.dataSource.data = this.visibleClaims;
  }

  isExpanded(element: ClaimItem): boolean {
    return this.expandedElement?.claimID === element.claimID;
  }

  toggle(element: ClaimItem): void {
    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  getStatusDisplayText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'under_review':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'new':
        return 'Under Review';
      default:
        return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'under_review':
        return 'status-under-review';
      case 'rejected':
        return 'status-rejected';
      case 'new':
        return 'status-under-review';
      default:
        return 'status-default';
    }
  }

  getAdjustmentMethod(claim: ClaimItem): string {
    return claim.adjustmentMethod
  }

  hasPhotos(claim: ClaimItem): boolean {
    return true;
  }

  hasQuote(claim: ClaimItem): boolean {
    return true;
  }

  hasDeed(claim: ClaimItem): boolean {
    return claim.deed ?? false;
  }

  getReviewedDate(claim: ClaimItem): string {
    return claim.modifiedAt;
  }

  downloadReport(event: Event, claim: ClaimItem): void {
    event.stopPropagation();
    if (!claim.claimID) {
      console.error('No claim ID available for download');
      return;
    }

    this.claimListService.getClaimReportDownloadUrls(claim.claimID, 'docx')
      .then(response => {
        if (!response?.files || response.files.length === 0) {
          console.warn('No files available for download');
          return;
        }

        response.files.forEach(file => {
          this.downloadFile(file.signedURL, file.fileName);
        });
      })
      .catch(error => {
        console.error('Failed to get download URLs:', error);
      });
  }

  private downloadFile(url: string, fileName: string): void {
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.target = '_blank';
      anchor.style.display = 'none';

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error(`Failed to download file ${fileName}:`, error);
    }
  }

  pageChanged($event: PageEvent) {
    this.onPageChange.emit($event);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.visibleClaims = this.claims?.filter(c => {
      return JSON.stringify(Object.values(c)).toLowerCase().includes(filterValue)
    }) ?? []
    this.dataSource.data = this.visibleClaims
  }

  protected readonly ObjectType = ObjectType;
}
